import { useState, useEffect } from 'react'
import { EmptyState } from 'src/components/empty-state'
import { AssistantHint } from 'src/components/assistant-hint'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { PlusIcon } from '@heroicons/react/24/outline'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { ContentPane } from 'src/components/layout/content-pane'
import { Input } from 'src/components/input'
import { Button } from 'src/components/button'
import { Spinner } from 'src/components/spinner'
import { PaginationComponent } from 'src/components/pagination'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { CreatePaymentAccountModal } from 'src/components/payment-accounts/create-payment-account-modal'
import { paymentAccountQueries } from 'src/services/payment-accounts/queries'
import { paymentAccountApi } from 'src/services/payment-accounts/api'
import { PaymentAccount } from 'src/services/payment-accounts/models'
import { usePagination } from 'src/hooks/use-pagination'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { useViewMode } from 'src/hooks/use-view-mode'
import { ItemCollection } from 'src/components/layout/item-collection'
import { ListToolbar } from 'src/components/layout/list-toolbar'
import {
  PaymentAccountItem,
  PaymentAccountListHeader,
} from 'src/components/payment-accounts/payment-account-item'

export const PaymentAccountsPage = () => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<PaymentAccount | undefined>(undefined)
  const [viewMode, setViewMode] = useViewMode('payment-accounts', 'list')
  const { pagination, paginationCallback, goToNextPage, goToPrevPage, goToPage, resetPagination } =
    usePagination()

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: (id: string) => paymentAccountApi.remove(id),
    queryKey: paymentAccountQueries.all(),
  })

  const openCreate = () => {
    setEditingAccount(undefined)
    setIsModalOpen(true)
  }

  const openEdit = (account: PaymentAccount) => {
    setEditingAccount(account)
    setIsModalOpen(true)
  }

  useEffect(() => {
    resetPagination()
  }, [search, resetPagination])

  const { data, isLoading } = useQuery(
    paymentAccountQueries.list({
      filters: { search: search || undefined },
      pagination,
      paginationCallback,
    }),
  )

  const accounts = data?.results || []

  return (
    <>
      <PageHeader
        title={t('paymentAccountsPage.title')}
        actions={
          <Button
            variant="primary"
            icon={<PlusIcon />}
            onClick={openCreate}
            data-testid="payment-account-new"
          >
            {t('paymentAccountsPage.newAccount')}
          </Button>
        }
      >
        <Input
          placeholder={t('paymentAccountsPage.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="payment-account-search"
        />
      </PageHeader>
      <MainContent>
        <ContentPane>
          <ListToolbar count={data?.count} viewMode={viewMode} onViewModeChange={setViewMode} />
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : accounts.length === 0 ? (
            <EmptyState
              title={t('paymentAccountsPage.emptyState')}
              description={!search ? t('paymentAccountsPage.emptyStateAdd') : undefined}
              className="py-12"
              assistantHint={
                !search && (
                  <AssistantHint
                    id="payment-accounts-empty"
                    text={t('assistantHint.paymentAccounts')}
                  />
                )
              }
              data-testid="empty-state"
            />
          ) : (
            <>
              {viewMode === 'list' && <PaymentAccountListHeader />}

              <ItemCollection viewMode={viewMode} className="mt-2">
                {accounts.map((account: PaymentAccount) => (
                  <PaymentAccountItem
                    key={account.id}
                    account={account}
                    viewMode={viewMode}
                    onEdit={openEdit}
                    onDelete={(a) => requestDelete(a.id, a.name)}
                  />
                ))}
              </ItemCollection>

              <PaginationComponent
                pagination={pagination}
                isLoading={isLoading}
                onNextPage={goToNextPage}
                onPrevPage={goToPrevPage}
                onGoToPage={goToPage}
                className="mt-4"
              />
            </>
          )}
        </ContentPane>
      </MainContent>

      <CreatePaymentAccountModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => setIsModalOpen(false)}
        paymentAccount={editingAccount}
      />

      <ConfirmDeleteDialog {...dialogProps} />
    </>
  )
}
